import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Card, CardContent, Avatar, Divider, LinearProgress, Skeleton, useTheme, alpha, TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PlanChip from '../../PlanChip';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { styled } from '@mui/material/styles';
import PaymentsService from '../../../services/PaymentsService';
import PaymentService from '../../../services/PaymentService';
import { getSession } from '../../../utils/authUtils';
import logger from '../../../utils/logger';
import { API_BASE_URL } from '../../../config/api';
import { useProfilePlan } from '../../../contexts/ProfilePlanContext';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'captured', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'created', label: 'Pending' },
  { value: 'refunded', label: 'Refunded' }
];

const subscriptionOptions = [
  { value: '', label: 'All Subscriptions' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'ending_soon', label: 'Ending Soon (7 days)' },
  { value: 'pending_renewal', label: 'Pending Renewal' }
];

// Plan badge configuration moved to shared `PlanChip` component

// Status badge configurations
// Status badge configurations
const statusConfig = {
  captured: { color: '#2e7d32', bg: '#e8f5e9', icon: CheckCircleIcon, label: 'Success' },
  failed: { color: '#d32f2f', bg: '#ffebee', icon: CancelIcon, label: 'Failed' },
  refunded: { color: '#0288d1', bg: '#e1f5fe', icon: RefreshIcon, label: 'Refunded' },
  ending_soon: { color: '#ff9800', bg: '#fff8e1', icon: WarningIcon, label: 'Renewal Pending' }
};

const TitleIconContainer = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(244, 63, 94, 0.4)',
}));

const StatCardStyled = styled(Card)(({ theme }) => ({
  padding: '20px',
  borderRadius: '16px',
  background: '#fff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '12px',
  background: bgcolor || 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
}));

const ThStyled = styled(TableCell)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)',
  color: '#ffffff',
  fontWeight: 600,
  padding: '16px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const TdStyled = styled(TableCell)(({ theme }) => ({
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#f8f9ff',
  },
}));

export default function PaymentsPage() {
  const theme = useTheme();
  const [payments, setPayments] = useState([]);
  const [planByGmb, setPlanByGmb] = useState({});
  const { loadProfileSubscription } = useProfilePlan();
  const [mandateMap, setMandateMap] = useState({}); // subscriptionId -> mandate info
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const lastUserPageRef = useRef(0);

  // refs to keep latest pagination values for intervals / sockets
  const pageRef = useRef(page);
  const rowsPerPageRef = useRef(rowsPerPage);
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { rowsPerPageRef.current = rowsPerPage; }, [rowsPerPage]);

  // Helper to check subscription status
  const getSubscriptionStatus = (p) => {
    if (p.status !== 'captured') return null;
    const endRaw = p.subscription_end_at || p.subscriptionEndAt;
    if (!endRaw) return null;
    try {
      const endDate = new Date(endRaw);
      const now = new Date();
      const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      if (daysUntilEnd < 0) return 'expired';
      if (daysUntilEnd <= 7) return 'ending_soon';
      return 'active';
    } catch (_) { return null; }
  };

  // Filter payments based on subscription status
  const filteredPayments = payments.filter(p => {
    if (!subscriptionFilter) return true;
    const subStatus = getSubscriptionStatus(p);
    if (subscriptionFilter === 'pending_renewal') {
      return subStatus === 'ending_soon' || subStatus === 'expired';
    }
    return subStatus === subscriptionFilter;
  });

  // Stats (use all payments, not just filtered/current page)
  const [allStats, setAllStats] = useState({
    totalRevenue: 0,
    capturedCount: 0,
    failedCount: 0,
    pendingRenewalCount: 0,
    total: 0
  });

  // Fetch stats for all payments (unfiltered)
  const fetchAllStats = async () => {
    try {
      const resp = await PaymentsService.fetchPayments({ limit: 10000, offset: 0 });
      // Normalize different response shapes (axios, api wrapper, legacy)
      const payload = resp?.data ?? resp;
      const all = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : (payload?.payments || payload?.items || []));
      const list = Array.isArray(all) ? all : [];

      const totalRevenue = list.reduce((sum, p) => {
        if (p.status !== 'captured') return sum;

        // Parse notes to check for deleted/cancelled/subscription flags
        let notes = {};
        try {
          notes = typeof p.notes === 'string' ? JSON.parse(p.notes) : (p.notes || {});
        } catch (e) { notes = {}; }

        const isDeleted = notes.deleted === true || notes.deleted === 'true';
        const isCancelled = notes.cancelled === true || notes.cancelled === 'true';

        if (isDeleted || isCancelled) return sum;

        return sum + parseFloat(p.amount || 0);
      }, 0);

      const capturedCount = list.filter(p => (p.status || '').toString() === 'captured').length;
      const failedCount = list.filter(p => ['created', 'failed'].includes((p.status || '').toString())).length;
      const pendingRenewalCount = list.filter(p => getSubscriptionStatus(p) === 'ending_soon').length;
      setAllStats({
        totalRevenue,
        capturedCount,
        failedCount,
        pendingRenewalCount,
        total: list.length
      });
    } catch (e) {
      setAllStats({ totalRevenue: 0, capturedCount: 0, failedCount: 0, pendingRenewalCount: 0, total: 0 });
    }
  };

  useEffect(() => { fetchAllStats(); }, []);

  const fetchPayments = async (pageArg = page, perPage = rowsPerPage) => {
    console.groupCollapsed('[PaymentsPage] fetchPayments called');
    console.log('params', { pageArg, perPage, q, status, subscriptionFilter, pageState: page });
    console.trace();
    console.groupEnd();
    setLoading(true);
    let offset = 0;
    try {
      console.log('[PaymentsPage] Fetching payments...');
      offset = parseInt(pageArg, 10) * parseInt(perPage, 10);
      const resp = await PaymentsService.fetchPayments({ q: q || undefined, status: status || undefined, limit: perPage, offset });
      console.log('[PaymentsPage] Response:', resp);
      if (resp && resp.success) {
        console.log('[PaymentsPage] Loaded', resp.data?.length, 'payments');
        const sorted = (resp.data || []).slice().sort((a, b) => getPaymentTimestamp(b) - getPaymentTimestamp(a));
        setPayments(sorted);
        setTotalCount(resp.total || 0);
        try { loadMandatesForPayments(resp.data || []); } catch (_) { }
        // Keep global stats up-to-date (non-blocking)
        try { fetchAllStats().catch(() => { }); } catch (_) { }
      } else if (resp && resp.data) {
        const sorted = (resp.data || []).slice().sort((a, b) => getPaymentTimestamp(b) - getPaymentTimestamp(a));
        setPayments(sorted);
        setTotalCount(resp.total || 0);
        try { loadMandatesForPayments(resp.data || []); } catch (_) { }
        try { fetchAllStats().catch(() => { }); } catch (_) { }
      }
    } catch (err) {
      console.error('[PaymentsPage] fetch error:', err?.status, err?.message, err);
      logger.error('[PaymentsPage] fetch error:', err);
      try {
        const session = getSession();
        const userId = session?.user?.id || session?.id || session?.user_id || null;
        if (err && (err.status === 401 || err.status === 403) && userId) {
          const userResp = await PaymentService.getUserPayments(userId, { status: status || undefined, limit: perPage, offset });
          if (userResp?.payments) {
            const sorted = (userResp.payments || []).slice().sort((a, b) => getPaymentTimestamp(b) - getPaymentTimestamp(a));
            setPayments(sorted);
          } else if (userResp?.data) {
            const sorted = (userResp.data || []).slice().sort((a, b) => getPaymentTimestamp(b) - getPaymentTimestamp(a));
            setPayments(sorted);
          }
        }
      } catch (e) {
        logger.error('[PaymentsPage] fallback error:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMandatesForPayments = async (paymentsList) => {
    if (!paymentsList || paymentsList.length === 0) return;
    const subs = [];
    for (const p of paymentsList) {
      try {
        const n = parseNotes(p);
        const sid = n?.subscription_id || n?.razorpay_subscription_id || (p.subscription && p.subscription.id) || null;
        if (sid && !mandateMap[sid]) subs.push(sid);
      } catch (e) { }
    }
    if (subs.length === 0) return;
    try {
      const fetches = subs.map(sid => fetch(`/api/v1/customer/subscription/mandate-status/${sid}`, { credentials: 'include' })
        .then(r => r.json()).then(d => ({ sid, d })).catch(err => ({ sid, err })));
      const results = await Promise.all(fetches);
      const next = { ...mandateMap };
      for (const r of results) {
        if (r && r.sid && r.d) next[r.sid] = r.d;
      }
      setMandateMap(next);
    } catch (e) {
      console.warn('[PaymentsPage] fetch mandates error', e);
    }
  };

  // Helper to safely parse notes JSON
  const parseNotes = (p) => {
    if (!p) return {};
    try {
      if (p.notes && typeof p.notes === 'string') return JSON.parse(p.notes);
      return p.notes || {};
    } catch (_) {
      return {};
    }
  };

  // Derive a numeric timestamp for sorting payments (prefer paid_at then created_at)
  const getPaymentTimestamp = (p) => {
    if (!p) return 0;
    const cand = p.paid_at || p.paidAt || p.updated_at || p.updatedAt || p.created_at || p.createdAt || p.created || null;
    try {
      const t = cand ? new Date(cand).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    } catch (_) { return 0; }
  };

  const getPayeeEmail = (p) => {
    const n = parseNotes(p);
    return p.customer_email || n.payeeGmail || n.payee_gmail || n.user_email || n.userEmail || n.email || '';
  };

  const getPayeePhone = (p) => {
    const n = parseNotes(p);
    return p.customer_phone || n.payeeMobile || n.payee_mobile || n.user_phone || n.userPhone || n.phone || '';
  };

  const getGmbBusiness = (p) => {
    const n = parseNotes(p);
    return p.gmb_profile_business || n.businessName || n.profile_name || n.gmb_profile_business || '';
  };

  const getPlan = (p) => {
    const n = parseNotes(p);
    // Prefer explicit purchased plan fields (minimal-risk: use payment-scoped data)
    if (p && (p.purchased_plan || p.purchased_plan_id)) {
      const pp = p.purchased_plan || p.purchased_plan_id;
      if (pp) {
        const s = String(pp).toLowerCase();
        if (s.includes('basic') || s.includes('starter')) return 'basic';
        if (s.includes('adv') || s.includes('advance') || s.includes('advanced')) return 'advance';
        if (s.includes('pro') || s.includes('premium') || s.includes('business')) return 'pro';
        // if it's a razorpay plan id like plan_R..., try to detect keywords
        if (s.startsWith('plan_')) {
          if (s.includes('basic')) return 'basic';
          if (s.includes('advance')) return 'advance';
          if (s.includes('pro')) return 'pro';
        }
      }
    }
    // Prefer server-resolved plan if present (fallback)
    if (p && (p.resolved_plan || p.plan || p.plan_id || p.planCode)) {
      const rp = (p.resolved_plan || p.plan || p.plan_id || p.planCode);
      if (rp) {
        const s = String(rp).toLowerCase();
        if (s.includes('basic') || s.includes('starter')) return 'basic';
        if (s.includes('adv') || s.includes('advance') || s.includes('advanced')) return 'advance';
        if (s.includes('pro') || s.includes('premium') || s.includes('business')) return 'pro';
      }
    }
    // Check nested notes object too (some payments have notes.notes)
    const nestedNotes = n.notes || {};
    // Collect possible plan fields from different payment note shapes
    const candidates = [
      n.plan, n.profile_plan, n.selectedPlan, n.tier, n.plan_id, n.planId, n.planCode, n.plan_code,
      n.plan_name, n.planName, n.profilePlan, n.profile_plan_key, nestedNotes.profile_plan, nestedNotes.plan,
      nestedNotes.plan_code, nestedNotes.plan_id, nestedNotes.plan_after_trial, n.plan_after_trial, n.plan_after,
      (p.subscription && p.subscription.plan) || null
    ].filter(Boolean).map(v => (typeof v === 'string' ? v : (v.id || v.name || JSON.stringify(v))));

    if (candidates.length === 0) return null;

    // Normalize and map common variants to canonical keys used in planConfig
    for (let raw of candidates) {
      const normalized = String(raw).toLowerCase().trim();
      if (!normalized) continue;
      if (normalized.includes('basic')) return 'basic';
      if (normalized.includes('adv') || normalized.includes('advance') || normalized.includes('advanced')) return 'advance';
      if (normalized.includes('pro') || normalized.includes('premium') || normalized.includes('business')) return 'pro';
      // plan ids from Razorpay often start with 'plan_' - try to infer from known plan ids
      if (normalized.startsWith('plan_')) {
        if (normalized.includes('basic')) return 'basic';
        if (normalized.includes('advance')) return 'advance';
        if (normalized.includes('pro')) return 'pro';
      }
    }

    return null;
  };

  const getCoupon = (p) => {
    const n = parseNotes(p);
    const nestedNotes = n.notes || {};
    return {
      code: n.coupon_code || nestedNotes.coupon_code || null,
      percent: n.coupon_discount_percent || nestedNotes.coupon_discount_percent || 0,
      amount: n.coupon_discount_amount || nestedNotes.coupon_discount_amount || n.discountAmount || nestedNotes.discountAmount || 0
    };
  };

  const renderTrialInfo = (p) => {
    const token = p.trial_token || p.trialToken || null;
    const amount = parseFloat(p.amount || 0);
    const paid = p.status === 'captured';
    return (
      <Box>
        {p.is_trial ? (
          <Chip label="Trial" size="small" sx={{ backgroundColor: '#fff4e5', color: '#ff8c00', fontWeight: 700 }} />
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        )}
        {token && (
          <Typography variant="caption" display="block" color="text.secondary">{String(token).slice(-8)}</Typography>
        )}
        {token && paid && (
          <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon sx={{ fontSize: 14 }} /> Verified · ₹{amount.toLocaleString('en-IN')}
          </Typography>
        )}
        {token && !paid && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PendingIcon sx={{ fontSize: 14 }} /> {p.status || 'pending'}
          </Typography>
        )}
      </Box>
    );
  };

  const getBillingCycle = (p) => {
    const n = parseNotes(p);
    return n.billing_cycle || n.billingCycle || n.cycle || n.duration || 'monthly';
  };

  const getClientId = (p) => {
    const n = parseNotes(p);
    return p.user_id || p.userId || p.client_id || p.clientId || n.userId || n.user_id || n.clientId ||
      p.paymentUser?.id || '';
  };

  const getClientName = (p) => {
    const n = parseNotes(p);
    return p.paymentUser?.name || p.paymentUser?.full_name || n.userName || n.clientName || '';
  };

  const getClientEmail = (p) => {
    const n = parseNotes(p);
    return p.paymentUser?.email || n.userEmail || '';
  };

  const formatDate = (d) => {
    try {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch (_) { return '—'; }
  };

  const getPayDate = (p) => {
    // For captured payments, show paid_at; otherwise show created_at
    const d = p.status === 'captured'
      ? (p.paid_at || p.paidAt || p.updated_at || p.updatedAt)
      : (p.created_at || p.createdAt);
    return formatDate(d);
  };

  const getSubscriptionEnd = (p) => {
    // Only show subscription end for captured (successful) payments
    if (p.status !== 'captured') return '—';

    const stored = p.subscription_end_at || p.subscriptionEndAt;
    if (stored) return formatDate(stored);

    const n = parseNotes(p);
    const nestedNotes = n.notes || {};
    let duration = null;
    const candidates = [
      n.duration, n.billingCycle, n.billing_cycle, n.cycle, n.plan_duration,
      nestedNotes.billing_cycle, nestedNotes.duration
    ];
    for (const raw of candidates) {
      if (raw === undefined || raw === null) continue;
      const s = String(raw).toLowerCase();
      if (s.includes('month')) { duration = 30; break; }
      if (s.includes('year')) { duration = 365; break; }
    }
    if (!duration) return '—';
    // Use paid_at for captured payments (the actual payment capture time)
    const start = p.paid_at || p.paidAt;
    if (!start) return '—';
    try {
      const end = new Date(new Date(start).getTime() + duration * 24 * 60 * 60 * 1000);
      return formatDate(end);
    } catch (_) { return '—'; }
  };

  const handleChangePage = (event, newPage) => {
    lastUserPageRef.current = newPage;
    setPage(newPage);
    fetchPayments(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    lastUserPageRef.current = 0;
    setRowsPerPage(newSize);
    setPage(0);
    fetchPayments(0, newSize);
  };

  const isSubscriptionActive = (p) => {
    // Only captured payments have active subscriptions
    if (p.status !== 'captured') return false;
    const endRaw = p.subscription_end_at || p.subscriptionEndAt;
    if (!endRaw) return null;
    try {
      return new Date() <= new Date(endRaw);
    } catch (_) { return null; }
  };

  // Render plan badge - use shared PlanChip
  const renderPlanBadge = (p) => {
    const n = (() => {
      try { return p.notes && typeof p.notes === 'string' ? JSON.parse(p.notes) : p.notes || {}; } catch { return p.notes || {}; }
    })();

    // Prefer purchased plan (from this payment) first — this ensures badge shows what user actually bought
    const planFromPayment = getPlan(p); // getPlan now prefers purchased_plan/purchased_plan_id
    if (planFromPayment) {
      return (
        <PlanChip plan={planFromPayment} />
      );
    }

    // Fallback: authoritative plan by GMB profile key
    const profileKey = n.profile_key || n.profileKey || p.gmb_profile_key || n.gmb_profile_key || null;
    if (profileKey && planByGmb[String(profileKey)]) {
      const plan = planByGmb[String(profileKey)];
      if (!plan) return <Typography variant="caption" color="text.secondary">—</Typography>;
      return <PlanChip plan={plan} />;
    }

    return <Typography variant="caption" color="text.secondary">—</Typography>;
  };

  // Render status badge - includes renewal pending logic
  const renderStatusBadge = (p) => {
    const paymentStatus = p.status || p;
    const subStatus = getSubscriptionStatus(p);

    // If subscription is ending soon, show "Renewal Pending"
    if (subStatus === 'ending_soon' && paymentStatus === 'captured') {
      const config = statusConfig.ending_soon;
      const Icon = config.icon;
      return (
        <Chip
          icon={<Icon sx={{ fontSize: 16 }} />}
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.bg,
            color: config.color,
            fontWeight: 600,
            fontSize: '0.75rem',
            border: `1px solid ${alpha(config.color, 0.3)}`
          }}
        />
      );
    }

    const config = statusConfig[paymentStatus] || statusConfig.failed;
    const Icon = config.icon;
    return (
      <Chip
        icon={<Icon sx={{ fontSize: 16 }} />}
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          border: `1px solid ${alpha(config.color, 0.3)}`
        }}
      />
    );
  };

  // Render subscription status with pending renewal indicator
  const renderSubscriptionStatus = (p) => {
    const subStatus = getSubscriptionStatus(p);
    if (!subStatus) return <Typography variant="caption" color="text.secondary">—</Typography>;

    const statusStyles = {
      active: { color: '#2e7d32', label: 'Active' },
      expired: { color: '#d32f2f', label: 'Expired' },
      ending_soon: { color: '#ff9800', label: 'Renewal Pending' }
    };

    const style = statusStyles[subStatus] || statusStyles.expired;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%',
          backgroundColor: style.color,
          boxShadow: `0 0 8px ${style.color}`,
          animation: subStatus === 'ending_soon' ? 'pulse 1.5s infinite' : 'none'
        }} />
        <Typography variant="caption" sx={{ color: style.color, fontWeight: 600 }}>
          {style.label}
        </Typography>
        {subStatus === 'ending_soon' && (
          <WarningIcon sx={{ fontSize: 14, color: '#ff9800', ml: 0.5 }} />
        )}
      </Box>
    );
  };

  // Autopay refresh removed (temporary) - use backend `include_autopay` or manual checks in admin scripts.

  // Detail Row Component for Modal (Matching Employee View style)
  const DetailRow = ({ icon: Icon, label, value, color }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#121927', // Matching Employee DetailLabel color
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.5,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        {Icon && <Icon sx={{ fontSize: 14, color: color || '#f43f5e' }} />} {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          color: '#363838ff', // Matching Employee DetailValue color
          pl: Icon ? 3 : 0,
          wordBreak: 'break-all'
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );

  const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: 600,
    color: '#f43f5e',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  }));

  // Open details modal
  const openDetailsModal = (payment) => {
    setSelectedPayment(payment);
    setDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedPayment(null);
  };

  useEffect(() => {
    // Only fetch on mount, never auto-refresh
    fetchPayments(pageRef.current, rowsPerPageRef.current);
    // No interval, no polling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('[PaymentsPage] page state changed', { page });
  }, [page]);

  // Revert programmatic page changes back to the last user-selected page
  useEffect(() => {
    if (page !== lastUserPageRef.current) {
      console.warn('[PaymentsPage] Detected programmatic page change -> reverting to last user page', { page, lastUserPage: lastUserPageRef.current });
      // revert asynchronously to avoid interfering with the original setter
      setTimeout(() => setPage(lastUserPageRef.current), 0);
    }
  }, [page]);

  useEffect(() => {
    console.log('[PaymentsPage] loading state changed', { loading });
  }, [loading]);



  // Prefetch plans for payments that reference a GMB profile key
  useEffect(() => {
    let mounted = true;
    const missing = (payments || []).map(p => {
      // Possible keys in payment notes
      const n = (() => {
        try { return p.notes && typeof p.notes === 'string' ? JSON.parse(p.notes) : p.notes || {}; } catch { return p.notes || {}; }
      })();
      return n.profile_key || n.profileKey || p.gmb_profile_key || n.gmb_profile_key || null;
    }).filter(Boolean).map(k => k);

    const toFetch = Array.from(new Set(missing)).filter(k => !planByGmb[String(k)]);
    if (toFetch.length === 0) return;

    (async () => {
      const results = {};
      await Promise.all(toFetch.map(async (profileKey) => {
        try {
          const resp = await loadProfileSubscription(profileKey).catch(() => null);
          const plan = resp?.planCode || resp?.plan || (resp?.data && resp.data.plan) || null;
          if (mounted) results[String(profileKey)] = plan;
        } catch (e) {
          if (mounted) results[String(profileKey)] = null;
        }
      }));
      if (mounted) setPlanByGmb(prev => ({ ...prev, ...results }));
    })();

    return () => { mounted = false; };
  }, [payments, loadProfileSubscription]);

  // Socket/remotes disabled: removed all socket.io usage from this page per request.

  return (
    <Box sx={{ p: 4, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TitleIconContainer>
            <PaymentIcon fontSize="large" />
          </TitleIconContainer>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>Payment Management</Typography>
            <Typography variant="body2" color="text.secondary">Track and manage all subscription revenues</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchPayments(0, rowsPerPage); fetchAllStats(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>


      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by email, phone, order ID..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchPayments()}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#f43f5e' }} /></InputAdornment>,
              sx: { borderRadius: 2 }
            }}
            sx={{ flex: 1, minWidth: 280 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Subscription"
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value)}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
            {subscriptionOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={() => { lastUserPageRef.current = 0; setPage(0); fetchPayments(0, rowsPerPage); }}
            sx={{ background: 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)', borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={() => { setQ(''); setStatus(''); setSubscriptionFilter(''); lastUserPageRef.current = 0; setPage(0); fetchPayments(0, rowsPerPage); fetchAllStats(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 4 }} />}

      {/* Payments Table */}
      <Paper sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <ThStyled>Order Details</ThStyled>
                <ThStyled>Amount</ThStyled>
                <ThStyled>Plan</ThStyled>
                <ThStyled>Trial</ThStyled>
                <ThStyled>Status</ThStyled>
                <ThStyled>Payee Info</ThStyled>
                <ThStyled>Discount</ThStyled>
                <ThStyled>Business</ThStyled>
                <ThStyled>Subscription</ThStyled>
                <ThStyled>Date</ThStyled>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length > 0 ? filteredPayments.map((p) => (
                <StyledTableRow
                  key={p.order_id || p.id}
                  hover
                  onClick={() => openDetailsModal(p)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TdStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '8px',
                        background: '#fff1f2', display: 'flex',
                        alignItems: 'center', justifyContent: 'center'
                      }}>
                        <ReceiptLongIcon sx={{ color: '#f43f5e', fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          #{p.order_id?.slice(-8).toUpperCase() || '—'}
                        </Typography>
                        {p.payment_id && (
                          <Typography variant="caption" color="text.secondary">
                            ID: {p.payment_id.slice(-10)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TdStyled>
                  <TdStyled>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#10b981', lineHeight: 1.2 }}>
                      ₹{parseFloat(p.amount || 0).toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{getBillingCycle(p)}</Typography>
                  </TdStyled>
                  <TdStyled>{renderPlanBadge(p)}</TdStyled>
                  <TdStyled>{renderTrialInfo(p)}</TdStyled>
                  <TdStyled>{renderStatusBadge(p)}</TdStyled>
                  <TdStyled>
                    <Box>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                        {getPayeeEmail(p) || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getPayeePhone(p) || '—'}
                      </Typography>
                    </Box>
                  </TdStyled>
                  <TdStyled>
                    {(() => {
                      const coupon = getCoupon(p);
                      if (!coupon.code) {
                        return <Typography variant="caption" color="text.secondary">None</Typography>;
                      }
                      return (
                        <Box>
                          <Chip
                            label={coupon.code}
                            size="small"
                            sx={{
                              backgroundColor: '#f0fdf4',
                              color: '#166534',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                          {coupon.percent > 0 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {coupon.percent}% off
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </TdStyled>
                  <TdStyled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                      <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getGmbBusiness(p) || '—'}
                      </Typography>
                    </Box>
                  </TdStyled>
                  <TdStyled>
                    <Box>
                      {renderSubscriptionStatus(p)}
                      <Typography variant="caption" color="text.secondary">
                        Ends: {getSubscriptionEnd(p)}
                      </Typography>
                    </Box>
                  </TdStyled>
                  <TdStyled>
                    <Typography variant="body2" color="text.secondary">{getPayDate(p)}</Typography>
                  </TdStyled>
                </StyledTableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 10 }}>
                    <PaymentIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h6" color="#64748b">No payments found</Typography>
                    <Typography variant="body2" color="#94a3b8">Adjust your filters or try a different search</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #f1f5f9' }}
        />
      </Paper>

      {/* Payment Details Modal */}
      <Dialog open={detailsModalOpen} onClose={closeDetailsModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{
          p: 3, display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: '1px solid #f1f5f9'
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
          }}>
            <ReceiptLongIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>Payment Details</Typography>
            <Typography variant="caption" color="text.secondary">{selectedPayment?.order_id}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {selectedPayment && (
            <Box>
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 3, mb: 4, borderRadius: '12px', background: '#f8fafc', border: '1px solid #f1f5f9'
              }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>TRANSACTION AMOUNT</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#10b981', mt: 0.5 }}>
                    ₹{parseFloat(selectedPayment.amount || 0).toLocaleString('en-IN')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    {renderStatusBadge(selectedPayment)}
                    {renderPlanBadge(selectedPayment)}
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  {renderSubscriptionStatus(selectedPayment)}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
                    {getBillingCycle(selectedPayment)} billing cycle
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e6ebf2', backgroundColor: '#fff' }}>
                  <SectionTitle>
                    <PaymentIcon fontSize="small" /> PAYMENT INFO
                  </SectionTitle>
                  <DetailRow icon={ReceiptLongIcon} label="Order ID" value={selectedPayment.order_id} color="#f43f5e" />
                  <DetailRow icon={PaymentIcon} label="Payment ID" value={selectedPayment.payment_id} color="#10b981" />
                  <DetailRow icon={CalendarTodayIcon} label="Payment Date" value={getPayDate(selectedPayment)} color="#fb7185" />
                  <DetailRow icon={EventIcon} label="Expiry Date" value={getSubscriptionEnd(selectedPayment)} color="#ef4444" />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e6ebf2', backgroundColor: '#fff' }}>
                  <SectionTitle color="#3b82f6">
                    <PersonIcon fontSize="small" /> PAYEE INFO
                  </SectionTitle>
                  <DetailRow icon={EmailIcon} label="Email Address" value={getPayeeEmail(selectedPayment)} color="#3b82f6" />
                  <DetailRow icon={PhoneIcon} label="Mobile Number" value={getPayeePhone(selectedPayment)} color="#10b981" />
                  <DetailRow icon={AccountCircleIcon} label="User Identity" value={getClientId(selectedPayment)} color="#8b5cf6" />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e6ebf2', backgroundColor: '#fff' }}>
                  <SectionTitle color="#64748b">
                    <BusinessIcon fontSize="small" /> BUSINESS INFO
                  </SectionTitle>
                  <DetailRow icon={BusinessIcon} label="Business Name" value={getGmbBusiness(selectedPayment)} color="#64748b" />
                  <DetailRow icon={BusinessIcon} label="Location ID" value={selectedPayment.gmb_profile_location_id} color="#94a3b8" />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e6ebf2', backgroundColor: '#fff' }}>
                  <SectionTitle color="#f59e0b">
                    <StarIcon fontSize="small" /> PLAN DETAILS
                  </SectionTitle>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600, color: '#121927', display: 'block', mb: 0.5 }}>CURRENT PLAN</Typography>
                    {selectedPayment.purchased_plan || selectedPayment.purchased_plan_id ? (
                      <Box sx={{ mt: 0.5 }}><PlanChip plan={selectedPayment.purchased_plan || selectedPayment.purchased_plan_id} /></Box>
                    ) : (
                      <Typography variant="body2" fontWeight={600}>None</Typography>
                    )}
                  </Box>
                  <DetailRow icon={AccessTimeIcon} label="Billing Status" value={getBillingCycle(selectedPayment)} color="#0ea5e9" />
                </Paper>
              </Box>

              <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
                {(() => {
                  const notes = parseNotes(selectedPayment);
                  if (Object.keys(notes).length === 0) return null;
                  return (
                    <Box>
                      <SectionTitle>
                        <ReceiptLongIcon fontSize="small" /> TRANSACTION NOTES
                      </SectionTitle>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', background: '#fff1f2', border: '1px solid #ffe4e6' }}>
                        {Object.entries(notes).map(([key, val]) => {
                          // Handle nested objects/arrays by stringifying them, otherwise show raw
                          const displayVal = typeof val === 'object' ? JSON.stringify(val) : String(val);
                          // Humanize keys: replace underscores with spaces and capitalize
                          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                          return (
                            <DetailRow
                              key={key}
                              icon={key.toLowerCase().includes('auto') || key.toLowerCase().includes('sub') ? CheckCircleIcon : ReceiptLongIcon}
                              label={label}
                              value={displayVal}
                              color={key.toLowerCase().includes('auto') || key.toLowerCase().includes('sub') ? '#10b981' : '#f43f5e'}
                            />
                          );
                        })}
                      </Paper>
                    </Box>
                  );
                })()}

                {selectedPayment.razorpay_events && (
                  <Box>
                    <SectionTitle color="#64748b">
                      <EventIcon fontSize="small" /> WEBHOOK EVENTS
                    </SectionTitle>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', maxHeight: 200, overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {typeof selectedPayment.razorpay_events === 'string'
                          ? selectedPayment.razorpay_events
                          : JSON.stringify(selectedPayment.razorpay_events, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #f1f5f9', gap: 2 }}>
          <Button
            onClick={async () => {
              if (!selectedPayment) return;
              if (!window.confirm('Are you sure you want to delete this payment record?')) return;
              try {
                await PaymentsService.deletePayment(selectedPayment.id || selectedPayment.order_id);
                await fetchPayments();
                closeDetailsModal();
              } catch (err) { alert('Delete failed'); }
            }}
            color="error"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete Record
          </Button>
          <Button
            onClick={closeDetailsModal}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)',
              borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
